import { t } from '@lingui/core/macro';
import { loadingChunkRetry } from '@sit/client-shared';
import { isUuidV4 } from '@sit/core';
import { createBrowserRouter, redirect } from 'react-router';
import { config, intacctUrls } from './config/env';
import { EntriesApprovalsIndexLayout } from './containers/EntriesApprovals/Layout';
import NotFoundPage from './containers/NotFoundPage';
import { NotFoundMessage } from './containers/NotFoundPage/NotFoundMessage';
import type { MetaResolver } from './helpers/head-manager';
import { appendCurrentParamsToUrl } from './helpers/routing';
import { Root } from './root';
import { Component as DeepLinking } from './routes/deep-linking';
import * as EntriesApprovalsIndex from './routes/entries-approvals._index';
import { Component as ExternalAuth } from './routes/external-auth';
import * as Index from './routes/index';
import { Component as StaffTimesheets } from './routes/staff-timesheets';
import * as TimeApprovalsIndex from './routes/time-approvals._index';
import { Component as Timesheet } from './routes/timesheet.$id';
import { Component as Timesheets } from './routes/timesheets';

function redirectFromTo(from: string, to: string) {
  return function loader({ request }: { request: Request }) {
    const url = new URL(request.url);
    const prefix = from;
    const rest = url.pathname.startsWith(prefix) ? url.pathname.slice(prefix.length) : '';
    return redirect(`${to}${rest}${url.search}`);
  };
}

export const router = createBrowserRouter([
  {
    path: '*',
    Component: Root,
    handle: {
      meta: () => ({
        title: t`Sage Intelligent Time`,
      }),
    } satisfies { meta: MetaResolver },
    children: [
      {
        index: true,
        loader: Index.loader,
      },
      {
        path: 'approvals/*',
        loader: redirectFromTo('/approvals', '/time-approvals'),
        handle: {
          meta: () => ({
            title: t`Approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'data-sources',
        lazy: () => loadingChunkRetry(() => import('./routes/data-sources')),
        handle: {
          meta: () => ({
            title: t`Data sources | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources._index')),
          },
          {
            path: 'calendar-missing-permission',
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources.calendar-missing-permission')),
          },
          {
            path: 'time-assistant',
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources.time-assistant')),
          },
          {
            path: ':sourceType',
            lazy: () => loadingChunkRetry(() => import('./routes/data-sources.$sourceType')),
            children: [
              {
                index: true,
                lazy: () => loadingChunkRetry(() => import('./routes/data-sources.$sourceType._index')),
              },
              {
                path: 'success',
                lazy: () => loadingChunkRetry(() => import('./routes/data-sources.$sourceType.success')),
              },
              {
                path: '*',
                Component: NotFoundMessage,
              },
            ],
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'deep-linking',
        Component: DeepLinking,
        handle: {
          meta: () => ({
            title: t`Loading...`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'entries-approvals',
        Component: EntriesApprovalsIndexLayout,
        handle: {
          meta: () => ({
            title: t`Entries approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            loader: EntriesApprovalsIndex.loader,
          },
          {
            path: 'todo',
            lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType')),
            children: [
              {
                path: ':id/entry/:entry_id',
                children: [
                  {
                    path: '*',
                    lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id')),
                  },
                ],
              },
            ],
          },
          {
            path: 'done',
            lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType')),
            children: [
              {
                path: ':id/entry/:entry_id',
                children: [
                  {
                    path: '*',
                    lazy: () => loadingChunkRetry(() => import('./routes/entries-approvals.$approvalType.$id.entry.$entry_id')),
                  },
                ],
              },
            ],
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'external-auth',
        Component: ExternalAuth,
        handle: {
          meta: () => ({
            title: t`Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'forgot-password',
        lazy: () => loadingChunkRetry(() => import('./routes/forgot-password')),
        handle: {
          meta: () => ({
            title: t`Forgot password | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'home/*',
        loader: () => {
          window.location.href = intacctUrls.timeHomepage;
        },
      },
      {
        path: 'login',
        lazy: () => loadingChunkRetry(() => import('./routes/login')),
        handle: {
          meta: () => ({
            title: t`Login | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'privacy',
        lazy: () => loadingChunkRetry(() => import('./routes/privacy')),
        handle: {
          meta: () => ({
            title: t`Privacy Policy | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'rate-cards',
        lazy: () => loadingChunkRetry(() => import('./routes/rate-cards')),
        handle: {
          meta: () => ({
            title: t`Rate cards | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            Component: () => null,
          },
          {
            path: 'new',
            lazy: () => loadingChunkRetry(() => import('./routes/rate-cards.new')),
          },
          {
            path: ':rateCard',
            lazy: () => loadingChunkRetry(() => import('./routes/rate-cards.$rateCard')),
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      ...(config.VITE_APP_SHOW_LEAVE
        ? [
            {
              path: 'leave',
              lazy: () => loadingChunkRetry(() => import('./routes/leave')),
              handle: {
                meta: () => ({
                  title: t`Leave | Sage Intelligent Time`,
                }),
              } satisfies { meta: MetaResolver },
            },
          ]
        : []),
      {
        path: 'reset-password/:resetToken',
        lazy: () => loadingChunkRetry(() => import('./routes/reset-password.$resetToken')),
        handle: {
          meta: () => ({
            title: t`Reset password | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'settings/*',
        loader: redirectFromTo('/settings', '/time-preferences'),
        handle: {
          meta: () => ({
            title: t`Settings | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'staff-timesheets',
        Component: StaffTimesheets,
        handle: {
          meta: () => ({
            title: t`Staff timesheets | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'staff-time-clocks',
        lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks')),
        handle: {
          meta: () => ({
            title: t`Staff time clocks | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            Component: () => null,
          },
          {
            path: 'new',
            lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.new')),
          },
          {
            path: ':timeClockId',
            lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.$timeClockId')),
            children: [
              {
                path: 'breaks',
                lazy: () => loadingChunkRetry(() => import('./routes/staff-time-clocks.$timeClockId.breaks')),
              },
            ],
          },
        ],
      },
      {
        path: 'timesheets',
        Component: Timesheets,
        handle: {
          meta: () => ({
            title: t`Timesheets | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
      },
      {
        path: 'timesheet/:id',
        Component: Timesheet,
        handle: {
          meta: () => ({
            title: t`Timesheet | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            path: 'entry/:entry_id/*',
            lazy: () => loadingChunkRetry(() => import('./routes/timesheet.$id.entry.$entry_id')),
          },
        ],
      },
      {
        path: 'time-approvals',
        lazy: () => loadingChunkRetry(() => import('./routes/time-approvals')),
        handle: {
          meta: () => ({
            title: t`Time approvals | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            loader: TimeApprovalsIndex.loader,
          },
          {
            path: 'todo',
            lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$approvalType')),
          },
          {
            path: 'done',
            lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$approvalType')),
          },
        ],
      },
      {
        path: 'time-approvals/:id',
        lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id')),
        handle: {
          meta: () => ({
            title: t`Time approval | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        loader: ({ request }) => {
          const url = new URL(request.url);
          // Get the :id, validate that it's a UUID
          const id = url.pathname.split('/')[2];
          if (!id || !isUuidV4(id)) {
            const redirectUrl = appendCurrentParamsToUrl(url, '/time-approvals/todo');
            return redirect(redirectUrl);
          }
        },
        children: [
          {
            index: true,
            Component: () => null,
          },
          {
            path: 'entry/:entry_id/*',
            lazy: () => loadingChunkRetry(() => import('./routes/time-approvals.$id.entry.$entry_id')),
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: 'time-clocks',
        lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks')),
        handle: {
          meta: () => ({
            title: t`Time clocks | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            path: ':timeClockId',
            lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks.$id')),
            children: [
              {
                path: 'breaks',
                lazy: () => loadingChunkRetry(() => import('@web/routes/time-clocks.$id')),
              },
            ],
          },
        ],
      },
      {
        path: 'time-preferences',
        lazy: () => loadingChunkRetry(() => import('./routes/time-preferences')),
        handle: {
          meta: () => ({
            title: t`Time preferences | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        children: [
          {
            index: true,
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences._index')),
          },
          {
            path: 'time-gathering',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.time-gathering')),
          },
          {
            path: 'notifications',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.notifications')),
          },
          {
            path: 'security',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.security')),
          },
          {
            path: 'advanced',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.advanced')),
          },
          {
            path: 'download-app',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.download-app')),
          },
          {
            path: 'demo-settings',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.demo-settings')),
          },
          {
            path: 'overtime',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime')),
            children: [
              {
                index: true,
                lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime._index')),
              },
              {
                path: 'rulesets',
                lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime.rulesets')),
              },
              {
                path: 'employees',
                lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.overtime.employees')),
              },
              {
                path: '*',
                Component: NotFoundMessage,
              },
            ],
          },
          {
            path: 'time-tracking',
            lazy: () => loadingChunkRetry(() => import('./routes/time-preferences.time-tracking')),
          },
          {
            path: '*',
            Component: NotFoundMessage,
          },
        ],
      },
      {
        path: '*',
        handle: {
          meta: () => ({
            title: t`Page not found | Sage Intelligent Time`,
          }),
        } satisfies { meta: MetaResolver },
        Component: NotFoundPage,
      },
    ],
  },
]);
